<script>
	import { page } from "$app/stores";
	import { browser } from "$app/environment";
	import { onDestroy, onMount, tick } from "svelte";
	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import { faRotateLeft } from "@fortawesome/free-solid-svg-icons";
	import {
		slugify,
		capitalizeEachWord,
		getResponseState,
		getSectionState,
	} from "cando-util";

	import { translate } from "$lib/utilities/translate";
	import { fbConfig } from "$lib/data/generated/fbConfig";

	import { getSections } from "./getSections";
	import { getCurrentErrors } from "./getCurrentErrors";
	import { getResponsesDb, setResponsesDb } from "./getResponsesDb";
	import { uploadFiles } from "./uploadFiles";

	import Tabs from "$lib/components/Tabs.svelte";
	import Diags from "./Diags.svelte";
	import Question from "./Question.svelte";
	import FormReview from "./FormReview.svelte";
	import MarkedContent from "$lib/components/MarkedContent.svelte";
	import Alert from "$lib/components/Alert.svelte";
	import Chat from "./Chat.svelte";

	export let data;
	export let lang;
	export let col;
	export let preview;
	export let settingId;
	export let updateResponses = async (responses) => {
		return responses;
	};
	export let hideIframe = false;
	export let allowReset = true;
	export let allowHideTags = true;

	$: setting = data.content?.[col]?.[settingId] || {};

	$: sections = getSections(setting, lang, $page.url.pathname);

	$: formName =
		data.content?.[col]?.[settingId]?.general?.value?.formName?.value;
	$: collection =
		data.content?.[col]?.[settingId]?.general?.value?.collection?.value ||
		"submissions";
	$: aiAgent = Object.entries(data?.content?.settings?.aiAgents || {})
		.find(([k, v]) => v.value?.formName?.value === formName)
		?.at(1);

	$: hideTags = sections.at(0)?.hideTags?.value;
	const showTags = (hideTags) => {
		if (browser) {
			setTimeout(() => {
				for (const elem of document.querySelectorAll(
					'[id^="main-app-"]',
				)) {
					elem.classList.remove("hidden");
				}
				for (const tag of (hideTags || "")
					.split("+||+")
					.filter((t) => !!t)) {
					const elem = document.getElementById(tag);
					if (elem && allowHideTags) {
						elem.classList.add("hidden");
					}
				}
			}, 1);
		}
	};
	$: showTags(hideTags);

	let selectedTab;
	$: initSelectedTab(sections);
	const initSelectedTab = (s) => {
		if (selectedTab || !s?.[0]?.id) return;
		selectedTab = s[0].id;
	};

	$: questions = sections.find((s) => s.id === selectedTab)?.questions || [];

	let responses = {};

	let responseState = {};

	$: sectionState = getSectionState(sections, responses);

	$: sectionTabs = sections.map((s) => ({
		...s,
		disabled: !sectionState[s.id].enabled,
	}));

	// if we wanna pass formName I'm not sure we wanna use an onMount function.
	// we may just wanna do a watcher that updates responses from db...
	// maybe need to do some kinda flag then that has this load run after onMount and when formName exists.
	onMount(async () => {
		if (browser) {
			const formName =
				data.content?.[col]?.[settingId]?.general?.value?.formName
					?.value;
			const collection =
				data.content?.[col]?.[settingId]?.general?.value?.collection
					?.value || "submissions";
			responses = await getResponsesDb(
				col,
				settingId,
				formName,
				collection,
				appVersion,
				buildVersion,
			);
			responseState = getResponseState(
				sections,
				responses,
				undefined,
				true,
			);
			responseState = Object.entries(responseState).reduce(
				(a, [k, v]) => ({ ...a, [k]: { ...v, isValid: undefined } }),
				{},
			);
		}
	});

	onDestroy(() => {
		if (browser) {
			for (const elem of document.querySelectorAll('[id^="main-app-"]')) {
				elem.classList.remove("hidden");
			}
		}
	});

	const handleChange = async (question) => {
		responses = await updateResponses(responses);

		if (!responseState[question.questionId])
			responseState[question.questionId] = {};

		responseState = getResponseState(sections, responses, question);

		const formName =
			data.content?.[col]?.[settingId]?.general?.value?.formName?.value;
		const collection =
			data.content?.[col]?.[settingId]?.general?.value?.collection
				?.value || "submissions";
		await setResponsesDb(
			col,
			settingId,
			formName,
			collection,
			responses,
			appVersion,
			buildVersion,
		);
	};

	const persistResponses = async (currentResponses = responses) => {
		await setResponsesDb(
			col,
			settingId,
			formName,
			collection,
			currentResponses,
			appVersion,
			buildVersion,
		);
	};

	const handleChatMessages = async (messages = []) => {
		responses = await updateResponses({
			...responses,
			messages,
		});
		await persistResponses(responses);

		return responses;
	};

	const handleChatSession = async ({
		conversationId,
		chatResponseFormat,
		model,
	} = {}) => {
		responses = await updateResponses({
			...responses,
			conversationId,
			chatResponseFormat,
			model,
		});
		await persistResponses(responses);

		return responses;
	};

	const handleChatUpdate = async ({
		fieldValues = {},
		changedFields = [],
	} = {}) => {
		const questionsById = sections
			.flatMap((section) => section.questions || [])
			.reduce(
				(acc, question) => ({
					...acc,
					[question.questionId]: question,
				}),
				{},
			);
		const fieldIds = changedFields.length
			? changedFields
			: Object.keys(fieldValues).filter(
					(key) =>
						fieldValues[key] !== undefined &&
						fieldValues[key] !== null &&
						fieldValues[key] !== "",
				);
		const nextResponses = { ...responses };
		let lastQuestion;
		let hasUpdates = false;

		for (const questionId of fieldIds) {
			const question = questionsById[questionId];
			const responseKey = question?.label?.value;
			if (!question || !responseKey) continue;

			const value = normalizeChatValue(question, fieldValues[questionId]);
			if (value === undefined) continue;

			nextResponses[responseKey] = value;
			lastQuestion = question;
			hasUpdates = true;
		}

		if (!hasUpdates) return responses;

		responses = await updateResponses(nextResponses);
		responseState = getResponseState(sections, responses, lastQuestion);
		await persistResponses(responses);

		return responses;
	};

	const normalizeChatValue = (question, value) => {
		const type = question.type?.value?.toLowerCase();
		const supportedTypes = [
			"text",
			"email",
			"phone",
			"tel",
			"textarea",
			"radio",
			"checkbox",
			"dropdown",
			"select",
			"date",
		];

		if (!supportedTypes.includes(type)) return undefined;

		if (type === "checkbox") {
			if (value === undefined || value === null || value === "")
				return [];
			return Array.isArray(value) ? value : [String(value)];
		}

		if (value === undefined || value === null) return "";
		if (typeof value === "object") return undefined;

		return String(value);
	};

	const handleReset = async () => {
		const formName =
			data.content?.[col]?.[settingId]?.general?.value?.formName?.value;
		const collection =
			data.content?.[col]?.[settingId]?.general?.value?.collection
				?.value || "submissions";
		responses = await getResponsesDb(
			col,
			settingId,
			formName,
			collection,
			appVersion,
			buildVersion,
			true,
		);

		responseState = getResponseState(sections, responses, undefined, true);
		responseState = Object.entries(responseState).reduce(
			(a, [k, v]) => ({ ...a, [k]: { ...v, isValid: undefined } }),
			{},
		);

		selectedTab = firstTab;
	};

	const handleBack = async () => {
		await tick();
		scrollFormToTop();

		if (previousTab) {
			selectedTab = previousTab;
		}
		console.log("go back");
	};

	let loading = false;

	$: activeTabs = sections.map((s) => s.id);
	$: firstTab = activeTabs[0];
	$: lastTab = activeTabs.at(-1);
	$: selectedTabIndex = activeTabs.indexOf(selectedTab);
	$: nextTab = activeTabs[selectedTabIndex + 1];
	$: previousTab = activeTabs[selectedTabIndex - 1];
	$: lastQuestion = questions.at(-1);
	$: currentErrors = getCurrentErrors(questions, responseState);
	$: chatMessages = Array.isArray(responses?.messages)
		? responses.messages
		: [];

	let progress = 0;
	const onProgress = (uploaded, total) => {
		progress = (uploaded / total) * 100;
	};

	const tabsFormId = `${col}-${settingId}-tabs-scroll-container`;
	const formHeadingId = slugify(`${col} ${settingId} Form Heading`);

	const handleNext = async (e) => {
		// if the last question is a set of checkboxes, this needs to be checked
		// otherwise, lastquestion for checkboxes is skipped since we're not sure if your done checking boxes
		responseState = getResponseState(
			sections,
			responses,
			lastQuestion,
			true, // sectionComeplete tells the function to check the last question if it's a set of checkboxes
			// by default, checkboxes aren't checked for invalid status since they could still be checking boxes
		);

		sectionState = getSectionState(sections, responses);

		console.log({ sectionState });

		const currentErrors = getCurrentErrors(questions, responseState);

		await tick();

		if (currentErrors.length > 0) {
			scrollFormToTop();
			return;
		}

		if (nextTab) {
			scrollFormToTop();
			selectedTab = nextTab;
		} else {
			console.log("all sections complete - Uploading files...");

			loading = true;
			const formData = await uploadFiles(responses, sections, onProgress);

			const formName =
				data.content?.[col]?.[settingId]?.general?.value?.formName
					?.value;
			const collection =
				data.content?.[col]?.[settingId]?.general?.value?.collection
					?.value || "submissions";
			await setResponsesDb(
				col,
				settingId,
				formName,
				collection,
				responses,
				appVersion,
				buildVersion,
			);

			console.log("Submit");
			const url = `https://us-central1-${fbConfig.projectId}.cloudfunctions.net/formsubmission`;
			const reqBody = { formData, sections };
			console.log({ reqBody });
			const request = {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(reqBody),
			};
			const response = await fetch(url, request);
			console.log({ response });
			const text = await response.text();
			console.log({ text });
			let json;
			try {
				json = JSON.parse(text);
			} catch (e) {
				console.error("JSON parse error:", e);
				showError(e.message);
				return;
			}
			console.log({ json });
			if (json.submissionResponse !== "Ok") {
				showError(
					`json.submissionResponse: ${json.submissionResponse}`,
				);
				progress = 0;

				loading = false;
				scrollFormToTop();
				return;
			}

			responses.submitted = true;
			responses.submittedAt = new Date().toISOString();

			await setResponsesDb(
				col,
				settingId,
				formName,
				collection,
				responses,
				appVersion,
				buildVersion,
			);

			progress = 0;

			scrollFormToTop();
			loading = false;
		}
	};

	const scrollFormToTop = () => {
		const container = document.getElementById("main-scrollable-container");
		const tabsElement = document.getElementById(formHeadingId);

		const targetOffset =
			tabsElement.getBoundingClientRect().top -
			container.getBoundingClientRect().top +
			container.scrollTop;

		container.scrollTo({
			top: targetOffset < 0 ? 0 : targetOffset,
			behavior: "smooth",
		});
	};

	let formSubmissionErrorMessage = "";
	const showError = (info) => {
		const message =
			"There was a problem submitting your application.<br />The firm has been notified and will reach out shortly.";
		formSubmissionErrorMessage = `${message}<br />${info}`;
		const elem = document.getElementById("form-submission-error");
		elem && elem.classList.remove("hidden");
	};

	$: showReview = responses?.submitted;

	$: buildVersion =
		data?.content?.settings?.["App Settings"]?.buildVersion?.value;
	$: appVersion = __APP_VERSION__;
</script>

<div
	class="flex justify-between"
	id={slugify(`${col} ${settingId} Form Heading`)}
>
	<div>
		{#if setting?.general?.value?.title?.value}
			<h2>
				{translate(setting?.general?.value?.title, lang)}
			</h2>
		{/if}
	</div>
</div>

{#if showReview}
	{#if setting?.general?.value?.confirmationMessage}
		<div class="flex justify-between">
			<div class="w-full space-y-2">
				<MarkedContent
					class="form-markdown"
					markedContent={translate(
						setting?.general?.value?.confirmationMessage,
						lang,
					)}
				/>
			</div>
			{#if allowReset}
				<div>
					<button
						aria-label="Reset Form"
						data-tip="Clear all form data"
						class={`btn btn-sm btn-primary p-2 tooltip tooltip-left`}
						on:click|preventDefault={handleReset}
					>
						<SvelteFa icon={faRotateLeft} size="1x" />
					</button>
				</div>
			{/if}
		</div>
	{/if}

	{#if aiAgent}
		<Chat
			{col}
			{settingId}
			{formName}
			{responses}
			messages={chatMessages}
			conversationId={responses?.conversationId}
			chatResponseFormat={responses?.chatResponseFormat}
			model={responses?.model}
			disabled={preview || loading}
			readonly={true}
			onFieldValues={handleChatUpdate}
			onMessages={handleChatMessages}
			onChatSession={handleChatSession}
		/>
	{/if}

	<FormReview {sections} {responses} {col} {lang} />
{:else}
	<div class="flex justify-between mb-4">
		<div class="w-full">
			<Tabs bind:selectedTab tabs={sectionTabs} id={tabsFormId} />
		</div>
		<div class="border-primary border-b-2">
			<button
				aria-label="Reset Form"
				data-tip="Clear all form data"
				class={`btn btn-sm btn-primary p-2 tooltip tooltip-left`}
				on:click|preventDefault={handleReset}
			>
				<SvelteFa icon={faRotateLeft} size="1x" />
			</button>
		</div>
	</div>

	{#if aiAgent}
		<Chat
			{col}
			{settingId}
			{formName}
			{responses}
			messages={chatMessages}
			conversationId={responses?.conversationId}
			chatResponseFormat={responses?.chatResponseFormat}
			model={responses?.model}
			disabled={preview || loading}
			onFieldValues={handleChatUpdate}
			onMessages={handleChatMessages}
			onChatSession={handleChatSession}
		/>
	{/if}

	{#if translate(sections.find((s) => s.id === selectedTab)?.heading, lang)}
		<div class="space-y-2">
			<MarkedContent
				class="form-markdown"
				markedContent={translate(
					sections.find((s) => s.id === selectedTab).heading,
					lang,
				)}
			/>
			&nbsp;
		</div>
	{/if}

	<form class="space-y-2 mx-auto w-full">
		<div class="text-error">
			{#if currentErrors.length > 0}
				<div>
					There {currentErrors.length > 1
						? `are ${currentErrors.length} errors on this page. Please fix them before continuing.`
						: `is ${currentErrors.length} error on this page. Please fix it before continuing.`}
				</div>
				<ul class="ml-4">
					{#each currentErrors as error}
						<li class="list-disc">
							{error.label} - {error.invalidMessage}
						</li>
					{/each}
				</ul>
			{/if}
		</div>

		<div
			class={`block m-2 ${progress === 0 ? "hidden" : ""}`}
			id={slugify(`${slugify(col)}-progress`)}
		>
			<div class="text-center">uploading...</div>
			<progress
				id={slugify(`${slugify(col)}-progressBar`)}
				class="progress"
				value={progress}
				max="100"
			></progress>
		</div>

		<fieldset class="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full">
			<legend class="sr-only"
				>{capitalizeEachWord(col || "")} Submission</legend
			>
			{#each questions as question}
				{#if question.label?.value !== undefined && responseState[question.label?.value]?.hideInForm !== true}
					<Question
						{question}
						bind:response={responses[question.label.value]}
						responseIsValid={responseState?.[question.questionId]
							?.isValid}
						required={responseState?.[question.questionId]
							?.required}
						readonly={responseState?.[question.questionId]
							?.readonly}
						disabled={responseState?.[question.questionId]
							?.disabled}
						hideInForm={responseState?.[question.questionId]
							?.hideInForm}
						{lang}
						{handleChange}
						{hideIframe}
					/>
				{/if}
			{/each}
		</fieldset>

		<Alert
			elemId="form-submission-error"
			message={formSubmissionErrorMessage}
			type="error"
			useHtml={true}
			useAlertClass={true}
		/>

		<div class="flex justify-between">
			<div>
				{#if selectedTab !== firstTab}
					<button
						class="btn btn-primary hover-ripple btn-lg order-2"
						on:click|preventDefault={handleBack}
					>
						<span class="text-lg sm:text-xl"> Back </span>
					</button>
				{/if}
			</div>
			<div>
				<button
					disabled={loading}
					on:click|preventDefault={handleNext}
					class={`btn btn-primary btn-lg order-1`}
				>
					<span
						class={`text-lg sm:text-xl ${loading ? "loading" : ""}`}
					></span>
					<span class={`text-lg sm:text-xl`}>
						{selectedTab === lastTab ? "Submit" : "Next"}
					</span>
				</button>
			</div>
		</div>
	</form>
{/if}

<Diags
	{activeTabs}
	{firstTab}
	{lastTab}
	{lang}
	{col}
	{preview}
	{settingId}
	{sections}
	{questions}
	{responses}
	{responseState}
	{sectionState}
	{selectedTab}
	{selectedTabIndex}
	{nextTab}
	{previousTab}
	{lastQuestion}
/>
